import clsx from "clsx";
import { ReactNode } from "react";

type Props = {
  title: string;
  /** Short qualifier under the title — the period, the scope, the unit. */
  subtitle?: string;
  /** Top-right slot: a headline figure, a legend, a filter. */
  action?: ReactNode;
  children: ReactNode;
  /** Shown instead of children on a genuine first load. */
  loading?: boolean;
  skeleton?: ReactNode;
  className?: string;
  /** Fixed body height so a card never resizes between skeleton and content. */
  bodyClassName?: string;
};

/**
 * The single frame every panel on the dashboard sits in.
 *
 * One card component rather than per-chart chrome is what makes the grid read as one
 * surface: identical radius, border, padding and header rhythm everywhere, so the eye
 * only has to parse the data. The title is plain ink — a gradient on eight headings
 * at once competes with the figures for attention.
 */
export default function ChartCard({
  title,
  subtitle,
  action,
  children,
  loading,
  skeleton,
  className,
  bodyClassName,
}: Props) {
  return (
    <section
      className={clsx(
        "group/card relative flex flex-col rounded-2xl border border-edge bg-surface",
        "shadow-card transition-colors duration-300 hover:border-edge-strong",
        className
      )}
    >
      <header className="flex items-start justify-between gap-4 px-5 pt-5 pb-4">
        <div className="min-w-0">
          <h2 className="text-[15px] font-semibold text-text-primary leading-tight truncate">
            {title}
          </h2>
          {subtitle ? (
            <p className="mt-0.5 text-[12px] font-medium text-light-gray truncate">
              {subtitle}
            </p>
          ) : null}
        </div>
        {action ? <div className="shrink-0">{action}</div> : null}
      </header>

      <div className={clsx("px-5 pb-5 flex-1 min-h-0", bodyClassName)}>
        {loading ? skeleton : children}
      </div>
    </section>
  );
}

/** Headline figure for a card's top-right slot. */
export function CardHeadline({
  children,
  caption,
}: {
  children: ReactNode;
  caption?: string;
}) {
  return (
    <div className="text-right">
      <div className="text-[20px] font-bold text-text-primary leading-none">
        {children}
      </div>
      {caption ? (
        <div className="mt-1 text-[11px] font-medium text-light-gray">{caption}</div>
      ) : null}
    </div>
  );
}
